// content/bosses/boss6.ts — BOSS 6: THE GLOOM SOVEREIGN (DESIGN §5, Zone 6).
//
//   "Verb: the final exam — it wields every zone's rule against you, tuned to
//    what you brought."
//
// A towering echo of the Warden, crowned and wrong, standing in the Heartchamber
// with the three gullets fused shut behind it. It teaches nothing. Every single
// thing it does is something the game already taught you, turned around:
//
//   THE BUILD-READING  at the bell it reads the weapon in your hands and picks
//                      the escort that answers it — Wailer rings against a blade,
//                      Shielded blades against a bow, moth chaff against a staff,
//                      homing casters against a maul. Announced, once, out loud.
//   P1 Memory of the Flood (100-70%)  two of the chamber's four edges drown on a
//                      surge rhythm, brine-glob echoes fall on marked ground, and
//                      Skippers pour from the fused north gates.
//   P2 Memory of the Court (70-40%)   its escorts start leaving husks on a
//                      two-second timer, and it takes up the rapier: the
//                      three-thrust red-glint cadence of the Twice-Crowned, one
//                      beat tighter.
//   THE HINGE          at 40% it tears out a HUSK-HEART and drops it mid-arena.
//                      Seal it inside five seconds or it takes twenty percent of
//                      itself back. Either way the third memory follows — the
//                      phase latches and never walks backwards.
//   P3 Memory of the Dark (<40%)      the chamber goes out to a lamp and two
//                      braziers. Its adds are hidden; IT IS NOT — it glows, it
//                      always glows, and the mask has a hole burnt in it where it
//                      stands. Its charges chain three deep behind the Mother of
//                      Lamps' glint-lane.
//
// Every literal comes from data/zones/zone6.ts (ZONE6_BOSS). What is written here
// is behaviour and drawing geometry. The floor systems — the flood's damage, the
// husk rule, the darkness mask, the light registry — are the ZONE's programs,
// reached through the bridge content/zones/zone6.ts exports, because a memory of
// zone 2 that ran different code from zone 2 would not be a memory.

import type { Draw, EliteRider, Enemy, RunCtx, Vec } from '../../types'
import { Boss, registerBoss } from '../../game/boss'
import { bossDefeated, registerBossArena, registerZoneContent } from '../../game/zones'
import { moveEnemy, registerEnemyBrain } from '../../game/enemies'
import { TILE } from '../../game/world'
import {
  ZONE6_BOSS,
  ZONE6_BOSS_ARENA,
  ZONE6_BOSS_ENEMIES,
  ZONE6_COLORS,
  ZONE6_PALE,
} from '../../data/zones/zone6'
import {
  z6DouseBrazier,
  z6FloodHurt,
  z6LightBrazier,
  zone6Arena,
  zone6Lights,
  type Z6FloodAcc,
} from '../zones/zone6'
import { emit } from '../../engine/particles'
import { PixelArt, drawSprite, registerPainter, registerRecipe, shade } from '../../engine/sprites'

const D = ZONE6_BOSS
const C = ZONE6_COLORS
const TAU = Math.PI * 2

/** Per-phase array read, clamped — phases are 1-based. */
function byPhase<T>(arr: readonly T[], phase: number): T {
  return arr[Math.max(0, Math.min(arr.length - 1, phase - 1))]
}

const posScratch: Vec = { x: 0, y: 0 }

// ===========================================================================
// THE HUSK-HEART — a body, so that everything already knows how to fight it.
// The Crown's lesson from boss 3: an ordinary enemy with a custom brain means
// every weapon, splash, burn trail, potion and Maul-stagger works on it without
// a line of special-case code. It does not move and it does not strike; it
// pulses, and it counts.
// ===========================================================================

registerEnemyBrain('z6-huskheart', (run, e, dt) => {
  e.vel.x = 0
  e.vel.y = 0
  e.bob += dt * 3
  moveEnemy(run, e, 0, 0, dt)
  if (e.bob % 0.3 < dt) {
    emit(e.pos.x, e.pos.y, { count: 3, color: C.heart, speed: 60, life: 0.4, size: 3, shape: 1 })
  }
})

// ===========================================================================
// THE GLOOM SOVEREIGN
// ===========================================================================

type FloodPhase = 'gap' | 'telegraph' | 'flooded'
type Combo = 'idle' | 'wind' | 'thrust' | 'gap' | 'recover'

interface Glob {
  on: boolean
  x0: number
  y0: number
  tx: number
  ty: number
  t: number
  dur: number
}

interface EscortPlan {
  body: string
  count: number
  elite: EliteRider | null
  note: string
}

class GloomSovereign extends Boss {
  readonly id = D.id
  /** read structurally by game/hud.ts for the boss bar */
  readonly name = D.name
  readonly phaseTicks = [D.phase3At, D.phase2At]

  private built = false
  private anim = 0

  /**
   * The phase LATCH. DESIGN §5 Boss 6 requires that P3 follows the husk-heart
   * "either way" — so a 20% heal must not be able to walk the fight back into
   * the Memory of the Court. Memories are entered once, in order.
   */
  private memory = 1

  // --- the build-reading
  private escort: EscortPlan = { body: 'z6-wailer', count: 2, elite: null, note: 'grief rings' }
  private escortT = 0

  // --- P1: the flood
  private floodPhase: FloodPhase = 'gap'
  private floodT = 0
  private active: number[] = []
  private pairCursor = 0
  private readonly floodAcc: Z6FloodAcc = { dmg: 0, push: 0 }

  // --- P1: the brine-glob echoes (hand-rolled, so the landing marker is real)
  private globs: Glob[] = []
  private globT = 0
  private globWind = 0

  // --- P1: the pours
  private pourT = 0
  private pourCursor = 0

  // --- P2: the rapier
  private combo: Combo = 'idle'
  private comboT = 0
  private comboW = 0
  private thrustsLeft = 0
  private stepT = 0
  private gapT = 0
  private recoverT = 0
  private lx = 1
  private ly = 0
  private glintT = 0

  // --- THE HINGE
  private heart: Enemy | null = null
  private heartT = 0
  private heartResolved = false

  // --- P3: the chained charges
  private chargeT = 0
  private chargeWind = 0
  private chargeGap = 0
  private chargeLeft = 0
  private charging = false
  private chargeDx = 0
  private chargeDy = 0
  private chargeLen = 0
  private chargeGone = 0
  private chargeHit = false

  private hurtT = 0
  private dying = false
  private deathT = 0
  private deathBurst = 0
  private paid = false
  private tookDamage = false
  private lastHp = -1

  constructor() {
    super(D.hp)
    this.radius = D.radius
    this.pos.x = D.centre.x
    this.pos.y = D.centre.y - 90
    for (let i = 0; i < 14; i++) this.globs.push({ on: false, x0: 0, y0: 0, tx: 0, ty: 0, t: 0, dur: 1 })
  }

  // ------------------------------------------------------------------ build

  private build(ctx: RunCtx): void {
    this.built = true
    zone6Arena.active = true
    zone6Arena.husks = false
    zone6Arena.dark = false
    zone6Arena.recoil = 0
    zone6Arena.heart.on = false
    for (const l of zone6Arena.lanes) l.on = false

    // the chamber's four edges, in world px
    zone6Arena.bands.length = 0
    for (const e of D.edges) {
      zone6Arena.bands.push({
        state: 0,
        x: e.x0 * TILE,
        y: e.y0 * TILE,
        w: (e.x1 - e.x0 + 1) * TILE,
        h: (e.y1 - e.y0 + 1) * TILE,
      })
    }

    // THE BUILD-READING, once, at the bell.
    const weapon = ctx.player.weaponId
    const plan = D.escorts.byWeapon[weapon] ?? D.escorts.byWeapon.cinderblade
    this.escort = {
      body: plan.body,
      count: plan.count,
      elite: (plan.elite as EliteRider | null) ?? null,
      note: plan.note,
    }
    this.escortT = byPhase(D.escorts.interval, 1) * 0.6

    this.floodPhase = 'gap'
    this.floodT = byPhase(D.flood.gap, 1)
    this.globT = byPhase(D.glob.interval, 1) * 0.7
    this.pourT = byPhase(D.pour.interval, 1) * 0.5
    this.comboT = byPhase(D.combo.interval, 1)
    this.lastHp = ctx.player.hp

    ctx.bark('THE GLOOM SOVEREIGN. It is wearing your shape.')
    ctx.bark(`${D.escorts.bark} (${this.escort.note})`)
    ctx.sfx('door')
    ctx.shake(10, 1.1)
  }

  // ----------------------------------------------------------------- phases

  protected onPhase(phase: number): void {
    const ctx = this.ctx
    if (!ctx) return
    if (phase === 2) {
      // MEMORY OF THE COURT: the husk rule comes on, and it takes up the rapier.
      zone6Arena.husks = true
      this.comboT = byPhase(D.combo.interval, 2) * 0.4
      ctx.bark('MEMORY OF THE COURT. What it kills for you does not stay killed.')
    } else if (phase === 3) {
      // MEMORY OF THE DARK: the chamber goes out, and the hinge lands.
      zone6Arena.dark = true
      zone6Lights.dark = true
      for (let i = 0; i < zone6Lights.braziers.length; i++) z6LightBrazier(i, D.dark.brazierSeconds)
      this.chargeT = byPhase(D.charge.interval, 3) * 0.6
      ctx.bark('MEMORY OF THE DARK. It still glows. Nothing else will.')
      this.dropHeart(ctx)
    }
    ctx.shake(8, 0.8)
    ctx.sfx('telegraph')
  }

  /** Health decides the memory — and a memory, once entered, is never left. */
  private applyPhase(): void {
    const f = this.healthFraction
    const want = f <= D.phase3At ? 3 : f <= D.phase2At ? 2 : 1
    if (want > this.memory) {
      this.memory = want
      this.setPhase(want)
    }
  }

  takeDamage(n: number): void {
    if (!this.alive || n <= 0 || this.dying) return
    const before = this.hp
    super.takeDamage(n)
    if (this.hp < before) this.hurtT = 0.12
    this.applyPhase()
  }

  // ----------------------------------------------------------------- update

  update(ctx: RunCtx, dt: number): void {
    this.tick(ctx, dt)
    if (!this.built) this.build(ctx)
    this.anim += dt
    if (this.hurtT > 0) this.hurtT -= dt
    if (this.glintT > 0) this.glintT -= dt

    // it is never invisible: the zone burns a hole in its own dark for it
    zone6Arena.sovereign.on = zone6Arena.dark
    zone6Arena.sovereign.x = this.pos.x
    zone6Arena.sovereign.y = this.pos.y
    zone6Arena.sovereign.r = D.dark.glowRadius

    if (this.dying) {
      this.updateDying(ctx, dt)
      return
    }
    if (!this.alive) {
      this.beginDeath(ctx)
      return
    }

    // no-hit watch, the same reading bosses 1-5 use: any drop in the Warden's
    // health during the fight spends the clean kill. Under ?invuln=1 nothing
    // reaches the player, so an invulnerable drive reads as untouched — true,
    // and noted where the test asserts it.
    if (this.lastHp >= 0 && ctx.player.hp < this.lastHp) this.tookDamage = true
    this.lastHp = ctx.player.hp

    this.applyPhase()
    const phase = this.memory

    this.updateHeart(ctx, dt)
    this.updateFlood(ctx, dt, phase)
    this.updateGlobs(ctx, dt, phase)
    if (phase === 1) this.updatePour(ctx, dt, phase)
    this.updateEscorts(ctx, dt, phase)
    if (phase >= 3) this.updateCharge(ctx, dt, phase)
    if (phase >= 2 && !this.charging) this.updateCombo(ctx, dt, phase)
    if (!this.charging && this.combo !== 'thrust' && this.combo !== 'wind') this.updateMove(ctx, dt)
    this.clampToChamber()
    this.publishBands()
  }

  /** It keeps a duelling band and circles it. It does not bullrush. */
  private updateMove(ctx: RunCtx, dt: number): void {
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const d = Math.hypot(dx, dy) || 1
    let mx = 0
    let my = 0
    if (d > D.walk.bandMax) {
      mx = dx / d
      my = dy / d
    } else if (d < D.walk.bandMin) {
      mx = -(dx / d)
      my = -(dy / d)
    }
    const s = Math.sin(this.anim * 0.8) * D.walk.strafe
    mx += (-dy / d) * s
    my += (dx / d) * s
    this.tryMove(ctx, mx * D.walk.speed * dt, my * D.walk.speed * dt)
  }

  /** Axis-separated move with a leading-edge wall check. */
  private tryMove(ctx: RunCtx, dx: number, dy: number): void {
    const r = this.radius * 0.6
    if (dx !== 0) {
      const nx = this.pos.x + dx
      if (!ctx.isWall(Math.floor((nx + Math.sign(dx) * r) / TILE), Math.floor(this.pos.y / TILE))) this.pos.x = nx
    }
    if (dy !== 0) {
      const ny = this.pos.y + dy
      if (!ctx.isWall(Math.floor(this.pos.x / TILE), Math.floor((ny + Math.sign(dy) * r) / TILE))) this.pos.y = ny
    }
  }

  /** It fights on the dry floor; the edges are for drowning, not for standing. */
  private clampToChamber(): void {
    const minX = 7 * TILE
    const maxX = 36 * TILE
    const minY = 4.5 * TILE
    const maxY = 16 * TILE
    if (this.pos.x < minX) this.pos.x = minX
    if (this.pos.x > maxX) this.pos.x = maxX
    if (this.pos.y < minY) this.pos.y = minY
    if (this.pos.y > maxY) this.pos.y = maxY
  }

  // ------------------------------------------- P1 — MEMORY OF THE FLOOD

  private bandOf(x: number, y: number): number {
    const tx = Math.floor(x / TILE)
    const ty = Math.floor(y / TILE)
    for (let i = 0; i < D.edges.length; i++) {
      const e = D.edges[i]
      if (tx >= e.x0 && tx <= e.x1 && ty >= e.y0 && ty <= e.y1) return i
    }
    return -1
  }

  /**
   * Two edges at a time, on an alternating beat. The floor's damage is the
   * ZONE's program (z6FloodHurt) — the same fifteen a second the Drowning Spur
   * charges, on the same tick spacing, because it is the same water.
   */
  private updateFlood(ctx: RunCtx, dt: number, phase: number): void {
    this.floodT -= dt

    if (this.floodPhase === 'gap') {
      if (this.floodT <= 0) {
        const pair = D.pairs[this.pairCursor % D.pairs.length]
        this.pairCursor++
        this.active.length = 0
        this.active.push(pair[0], pair[1])
        this.floodPhase = 'telegraph'
        this.floodT = byPhase(D.flood.telegraph, phase)
        this.telegraph('edge-flood', this.floodT)
        ctx.sfx('door')
        ctx.shake(2, 0.4)
      }
      return
    }

    if (this.floodPhase === 'telegraph') {
      if (this.floodT <= 0) {
        this.floodPhase = 'flooded'
        this.floodT = byPhase(D.flood.duration, phase)
        this.floodAcc.dmg = 0
        this.floodAcc.push = 0
        ctx.sfx('generator-die')
        ctx.shake(9, 0.5)
        ctx.hitstop(0.06)
      }
      return
    }

    const q = this.bandOf(ctx.player.pos.x, ctx.player.pos.y)
    const drowned = q >= 0 && this.active.indexOf(q) >= 0
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const l = Math.hypot(dx, dy) || 1
    z6FloodHurt(ctx, dt, drowned, this.floodAcc, dx / l, dy / l, D.flood)

    if (this.floodT <= 0) {
      this.floodPhase = 'gap'
      this.floodT = byPhase(D.flood.gap, phase)
      this.active.length = 0
      ctx.sfx('door')
    }
  }

  /** Hand the floor state to the zone mechanic, which draws it under the fight. */
  private publishBands(): void {
    const tele = this.floodPhase === 'telegraph'
    const dur = byPhase(D.flood.telegraph, this.memory)
    const k = tele ? 1 - Math.max(0, this.floodT) / Math.max(0.01, dur) : 0
    for (let i = 0; i < zone6Arena.bands.length; i++) {
      const b = zone6Arena.bands[i]
      if (this.active.indexOf(i) < 0) {
        b.state = 0
        continue
      }
      b.state = this.floodPhase === 'flooded' ? -1 : Math.max(0.001, k)
    }
  }

  // ------------------------------------------------------ the glob echoes

  private updateGlobs(ctx: RunCtx, dt: number, phase: number): void {
    for (const g of this.globs) {
      if (!g.on) continue
      g.t += dt
      if (g.t >= g.dur) {
        g.on = false
        this.globLand(ctx, g)
      }
    }
    if (phase >= 3) return

    if (this.globWind > 0) {
      this.globWind -= dt
      if (this.globWind <= 0) this.throwGlobs(ctx, phase)
      return
    }
    this.globT -= dt
    if (this.globT > 0) return
    this.globT = byPhase(D.glob.interval, phase)
    this.globWind = D.glob.windup
    this.telegraph('glob', D.glob.windup)
  }

  private throwGlobs(ctx: RunCtx, phase: number): void {
    const n = byPhase(D.glob.count, phase)
    let thrown = 0
    for (const g of this.globs) {
      if (thrown >= n) break
      if (g.on) continue
      g.on = true
      g.x0 = this.pos.x
      g.y0 = this.pos.y - 16
      const a = ctx.rng() * TAU
      const r = ctx.rng() * D.glob.scatter
      g.tx = ctx.player.pos.x + Math.cos(a) * r
      g.ty = ctx.player.pos.y + Math.sin(a) * r
      g.t = 0
      g.dur = D.glob.flightSeconds
      thrown++
    }
    if (thrown > 0) {
      ctx.sfx('shoot')
      emit(this.pos.x, this.pos.y - 10, { count: 12, color: D.glob.color, speed: 170, life: 0.3, size: 3, shape: 1 })
    }
  }

  private globLand(ctx: RunCtx, g: Glob): void {
    emit(g.tx, g.ty, {
      count: 20,
      color: D.glob.color,
      speed: 210,
      speedVar: 150,
      life: 0.45,
      lifeVar: 0.2,
      size: 4,
      grav: 220,
      shape: 1,
    })
    ctx.shake(2.4, 0.18)
    ctx.sfx('hit')
    const dx = ctx.player.pos.x - g.tx
    const dy = ctx.player.pos.y - g.ty
    if (dx * dx + dy * dy <= D.glob.splash * D.glob.splash) ctx.damagePlayer(D.glob.damage, 'brine-echo')
  }

  // ------------------------------------------------------------- the pours

  private liveChildren(ctx: RunCtx): number {
    let n = 0
    const list = ctx.enemies
    for (let i = 0; i < list.length; i++) if (list[i].alive && list[i].tags.z6boss !== undefined) n++
    return n
  }

  private updatePour(ctx: RunCtx, dt: number, phase: number): void {
    const interval = byPhase(D.pour.interval, phase)
    if (interval <= 0) return
    this.pourT -= dt
    if (this.pourT > 0) return
    this.pourT = interval
    const n = byPhase(D.pour.count, phase)
    for (let k = 0; k < n; k++) {
      if (this.liveChildren(ctx) >= D.pour.liveCap) return
      const t = D.pour.tiles[this.pourCursor % D.pour.tiles.length]
      this.pourCursor++
      posScratch.x = t.tx * TILE + TILE / 2 + (ctx.rng() - 0.5) * 24
      posScratch.y = t.ty * TILE + TILE / 2 + (ctx.rng() - 0.5) * 24
      const e = ctx.spawnEnemy(D.pour.body, posScratch, { elite: null })
      if (!e.alive) continue
      e.tags.z6boss = 1
      emit(posScratch.x, posScratch.y, { count: 10, color: C.caustic, speed: 140, life: 0.35, size: 3, shape: 1 })
    }
    ctx.sfx('door')
  }

  // ------------------------------------------------------ the escort waves

  private updateEscorts(ctx: RunCtx, dt: number, phase: number): void {
    this.escortT -= dt
    if (this.escortT > 0) return
    this.escortT = byPhase(D.escorts.interval, phase)
    if (this.liveChildren(ctx) >= D.escorts.liveCap) return
    for (let i = 0; i < this.escort.count; i++) {
      if (this.liveChildren(ctx) >= D.escorts.liveCap) return
      const a = (i / this.escort.count) * TAU + this.anim
      posScratch.x = this.pos.x + Math.cos(a) * 120
      posScratch.y = this.pos.y + Math.sin(a) * 90
      if (ctx.isWall(Math.floor(posScratch.x / TILE), Math.floor(posScratch.y / TILE))) {
        posScratch.x = this.pos.x
        posScratch.y = this.pos.y + 70
      }
      const e = ctx.spawnEnemy(this.escort.body, posScratch, { elite: this.escort.elite })
      if (!e.alive) continue
      e.tags.z6boss = 1
      emit(posScratch.x, posScratch.y, { count: 10, color: C.vein, speed: 150, life: 0.4, size: 3, shape: 1 })
    }
    ctx.sfx('generator-die')
  }

  // -------------------------------------------- P2 — MEMORY OF THE COURT

  private lockStep(ctx: RunCtx): void {
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const l = Math.hypot(dx, dy) || 1
    this.lx = dx / l
    this.ly = dy / l
    this.stepT = D.combo.stepTime
    this.glintT = 0.16
    emit(this.pos.x + this.lx * 26, this.pos.y + this.ly * 26, {
      count: 6,
      color: '#ff4a4a',
      speed: 160,
      life: 0.2,
      size: 3,
      angle: Math.atan2(this.ly, this.lx),
      spread: 1.1,
      shape: 1,
    })
  }

  private thrust(ctx: RunCtx): void {
    const dir = Math.atan2(this.ly, this.lx)
    const pdx = ctx.player.pos.x - this.pos.x
    const pdy = ctx.player.pos.y - this.pos.y
    const pd = Math.hypot(pdx, pdy)
    let inArc = pd <= D.combo.range + this.radius
    if (inArc && pd > 2) {
      let a = Math.atan2(pdy, pdx) - dir
      while (a > Math.PI) a -= TAU
      while (a < -Math.PI) a += TAU
      inArc = Math.abs(a) <= D.combo.arc / 2
    }
    if (inArc) ctx.damagePlayer(D.combo.damage, D.id)
    ctx.sfx('swing')
    emit(this.pos.x + this.lx * 30, this.pos.y + this.ly * 30, {
      count: 8,
      color: '#f0e0ff',
      speed: 200,
      life: 0.18,
      size: 3,
      angle: dir,
      spread: D.combo.arc,
      shape: 1,
    })
  }

  private updateCombo(ctx: RunCtx, dt: number, phase: number): void {
    if (this.combo === 'idle') {
      this.comboT -= dt
      if (this.comboT > 0) return
      const dx = ctx.player.pos.x - this.pos.x
      const dy = ctx.player.pos.y - this.pos.y
      if (Math.hypot(dx, dy) > D.walk.bandMax * 1.7) return // close first, then strike
      this.combo = 'wind'
      this.comboW = byPhase(D.combo.windup, phase)
      this.thrustsLeft = byPhase(D.combo.thrusts, phase)
      this.glintT = this.comboW
      this.telegraph('thrust', this.comboW)
      return
    }

    if (this.combo === 'wind') {
      this.comboW -= dt
      const dx = ctx.player.pos.x - this.pos.x
      const dy = ctx.player.pos.y - this.pos.y
      const l = Math.hypot(dx, dy) || 1
      this.tryMove(ctx, (-dx / l) * 26 * dt, (-dy / l) * 26 * dt)
      this.glintT = Math.max(this.glintT, this.comboW)
      if (this.comboW <= 0) {
        this.combo = 'thrust'
        this.lockStep(ctx)
      }
      return
    }

    if (this.combo === 'thrust') {
      this.stepT -= dt
      const sp = D.combo.dashSpeed * dt
      this.tryMove(ctx, this.lx * sp, this.ly * sp)
      if (this.stepT <= 0) {
        this.thrust(ctx)
        this.thrustsLeft--
        if (this.thrustsLeft > 0) {
          this.combo = 'gap'
          this.gapT = byPhase(D.combo.stepGap, phase)
        } else {
          this.combo = 'recover'
          this.recoverT = D.combo.recover
        }
      }
      return
    }

    if (this.combo === 'gap') {
      this.gapT -= dt
      if (this.gapT <= 0) {
        this.combo = 'thrust'
        this.lockStep(ctx)
      }
      return
    }

    this.recoverT -= dt
    if (this.recoverT <= 0) {
      this.combo = 'idle'
      this.comboT = byPhase(D.combo.interval, phase)
    }
  }

  // ------------------------------------------------------------ THE HINGE

  private dropHeart(ctx: RunCtx): void {
    posScratch.x = D.centre.x
    posScratch.y = D.centre.y + D.heart.offsetY
    const h = ctx.spawnEnemy(D.heart.defId, posScratch, { elite: null })
    this.heartResolved = false
    if (!h.alive) {
      // no heart, no hinge — fail safe toward the player's favour
      this.heartResolved = true
      return
    }
    // written by hand so the zone's stat scale can never drift the window
    h.maxHp = D.heart.hp
    h.hp = D.heart.hp
    h.tags.z6heart = 1
    this.heart = h
    this.heartT = D.heart.windowSeconds
    zone6Arena.heart.on = true
    zone6Arena.heart.x = h.pos.x
    zone6Arena.heart.y = h.pos.y
    zone6Arena.heart.t = this.heartT
    ctx.hitstop(0.18)
    ctx.shake(11, 0.9)
    ctx.sfx('death')
    ctx.bark('It tears out a husk-heart. FIVE SECONDS — seal it, or it takes itself back.')
    emit(h.pos.x, h.pos.y, {
      count: 34,
      color: C.heart,
      speed: 250,
      speedVar: 170,
      life: 0.8,
      lifeVar: 0.35,
      size: 4,
      drag: 0.88,
    })
  }

  private updateHeart(ctx: RunCtx, dt: number): void {
    if (this.heartResolved) return
    const h = this.heart
    if (!h) return
    if (!h.alive) {
      // SEALED. It gets nothing back, and the third memory still comes.
      this.heartResolved = true
      this.heart = null
      zone6Arena.heart.on = false
      ctx.hitstop(0.12)
      ctx.shake(6, 0.5)
      ctx.sfx('pickup')
      ctx.bark('Sealed. It does not get that back.')
      emit(this.pos.x, this.pos.y, { count: 24, color: C.huskGlow, speed: 220, life: 0.6, size: 4, shape: 1 })
      return
    }
    this.heartT -= dt
    zone6Arena.heart.x = h.pos.x
    zone6Arena.heart.y = h.pos.y
    zone6Arena.heart.t = this.heartT
    if (this.heartT > 0) return

    // The five seconds ran out: it takes twenty percent of itself back.
    this.heartResolved = true
    zone6Arena.heart.on = false
    h.kill(true)
    this.heart = null
    const heal = Math.round(this.maxHp * D.heart.healFraction)
    this.hp = Math.min(this.maxHp, this.hp + heal)
    ctx.hitstop(0.16)
    ctx.shake(12, 1)
    ctx.sfx('generator-die')
    ctx.bark(`The heart goes back in. ${heal} of it, returned.`)
    emit(this.pos.x, this.pos.y, {
      count: 40,
      color: C.heart,
      speed: 280,
      speedVar: 200,
      life: 0.9,
      lifeVar: 0.4,
      size: 5,
      drag: 0.88,
    })
  }

  // --------------------------------------------- P3 — MEMORY OF THE DARK

  private updateCharge(ctx: RunCtx, dt: number, phase: number): void {
    const lane = zone6Arena.lanes[0]

    if (this.charging) {
      const speed = byPhase(D.charge.speed, phase)
      const step = speed * dt
      this.pos.x += this.chargeDx * step
      this.pos.y += this.chargeDy * step
      this.chargeGone += step
      if (!this.chargeHit) {
        const d = Math.hypot(ctx.player.pos.x - this.pos.x, ctx.player.pos.y - this.pos.y)
        const pr = (ctx.player as unknown as { radius?: number }).radius ?? 10
        if (d < this.radius * 0.85 + pr) {
          this.chargeHit = true
          ctx.damagePlayer(D.charge.damage, 'sovereign-charge')
          ctx.pushPlayer(this.chargeDx * 280, this.chargeDy * 280)
          ctx.shake(6, 0.3)
        }
      }
      emit(this.pos.x, this.pos.y, { count: 2, color: C.mote, speed: 90, life: 0.35, size: 3, shape: 1 })
      if (this.chargeGone >= this.chargeLen) {
        this.charging = false
        lane.on = false
        this.chargeLeft--
        if (this.chargeLeft > 0) this.chargeGap = D.charge.chainGap
        else this.chargeT = byPhase(D.charge.interval, phase)
      }
      return
    }

    if (this.chargeGap > 0) {
      this.chargeGap -= dt
      if (this.chargeGap <= 0) this.armCharge(ctx, phase)
      return
    }

    if (this.chargeWind > 0) {
      this.chargeWind -= dt
      const total = byPhase(D.charge.windup, phase)
      lane.k = 1 - Math.max(0, this.chargeWind) / Math.max(0.01, total)
      if (this.chargeWind <= 0) {
        this.charging = true
        this.chargeGone = 0
        this.chargeHit = false
        lane.k = -1
        ctx.sfx('dash')
        ctx.shake(5, 0.25)
      }
      return
    }

    this.chargeT -= dt
    if (this.chargeT > 0) return
    this.chargeLeft = byPhase(D.charge.chain, phase)
    this.armCharge(ctx, phase)
  }

  private armCharge(ctx: RunCtx, phase: number): void {
    const dx = ctx.player.pos.x - this.pos.x
    const dy = ctx.player.pos.y - this.pos.y
    const l = Math.hypot(dx, dy) || 1
    this.chargeDx = dx / l
    this.chargeDy = dy / l
    this.chargeLen = Math.max(280, l + 200)
    this.chargeWind = byPhase(D.charge.windup, phase)
    const lane = zone6Arena.lanes[0]
    lane.on = true
    lane.k = 0
    lane.x0 = this.pos.x
    lane.y0 = this.pos.y
    lane.x1 = this.pos.x + this.chargeDx * this.chargeLen
    lane.y1 = this.pos.y + this.chargeDy * this.chargeLen
    this.telegraph('charge', this.chargeWind)
  }

  // ------------------------------------------------------------------ death
  //
  // The biggest moment in the game gets two full seconds of it: the chamber
  // stops, the Gloom recoils in staggered bursts, and only then does the payout
  // land. bossDefeated cuts the stair, and the stair out of zone 6 ends the run
  // as a win (game/game.ts advanceRoom).

  private beginDeath(ctx: RunCtx): void {
    this.dying = true
    this.deathT = 0
    this.deathBurst = 0
    this.alive = false
    this.hp = 0
    zone6Arena.husks = false
    zone6Arena.heart.on = false
    for (const l of zone6Arena.lanes) l.on = false
    for (const b of zone6Arena.bands) b.state = 0
    this.active.length = 0
    for (const g of this.globs) g.on = false

    // the chamber comes up: the dark it was holding lets go
    zone6Arena.dark = false
    zone6Lights.dark = false
    for (let i = 0; i < zone6Lights.braziers.length; i++) z6LightBrazier(i, D.dark.brazierSeconds)

    const list = ctx.enemies
    for (let i = list.length - 1; i >= 0; i--) if (list[i].alive) list[i].kill(true)

    ctx.hitstop(D.death.hitstop)
    ctx.shake(D.death.shake, D.death.shakeSeconds)
    ctx.sfx('generator-die')
    ctx.sfx('death')
    ctx.bark(D.death.bark)
    zone6Arena.recoil = 1
  }

  private updateDying(ctx: RunCtx, dt: number): void {
    this.deathT += dt
    // staggered bursts: the Gloom coming apart in stages, not one puff
    while (this.deathBurst < D.death.bursts && this.deathT >= this.deathBurst * D.death.burstGap) {
      const i = this.deathBurst++
      const a = (i / D.death.bursts) * TAU
      const x = this.pos.x + Math.cos(a) * (10 + i * 16)
      const y = this.pos.y + Math.sin(a) * (8 + i * 12)
      emit(x, y, {
        count: 40,
        color: i % 3 === 0 ? C.mote : i % 3 === 1 ? C.vein : C.heart,
        speed: 240 + i * 90,
        speedVar: 210,
        life: 1,
        lifeVar: 0.5,
        size: 5,
        drag: 0.88,
      })
      ctx.shake(7 - i, 0.4)
      ctx.sfx(i % 2 === 0 ? 'generator-die' : 'death')
      zone6Arena.recoil = Math.max(zone6Arena.recoil, 0.8)
    }
    if (this.deathT < D.death.seconds || this.paid) return

    this.paid = true
    ctx.featTick('boss6_kill')
    if (!this.tookDamage) {
      ctx.featTick('boss6_nohit')
      ctx.bark('Perfect descent. It never laid a hand on you.')
    }
    ctx.bark('The Gloom recoils. Hollowdeep is yours to leave.')
    bossDefeated(ctx)
  }

  // ----------------------------------------------------------------- render

  render(d: Draw): void {
    const ctx = d.ctx
    const t = this.anim
    if (this.heart) this.renderHeart(ctx)
    this.renderGlobs(ctx)

    // its own light: in the Memory of the Dark this is the only warm thing left,
    // and it is drawn UNDER the mask as well as punched through it, so the shape
    // reads either way.
    ctx.save()
    ctx.globalAlpha = 0.4
    ctx.fillStyle = '#000000'
    ctx.beginPath()
    ctx.ellipse(this.pos.x, this.pos.y + 40, 42, 13, 0, 0, TAU)
    ctx.fill()
    const glow = 0.22 + 0.1 * Math.sin(t * 2.2) + (zone6Arena.dark ? 0.2 : 0)
    ctx.globalAlpha = glow
    const g = ctx.createRadialGradient(this.pos.x, this.pos.y, 10, this.pos.x, this.pos.y, 150)
    g.addColorStop(0, 'rgba(201,166,255,0.55)')
    g.addColorStop(1, 'rgba(201,166,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(this.pos.x - 150, this.pos.y - 150, 300, 300)
    ctx.restore()

    if (this.dying) {
      const k = Math.max(0, 1 - this.deathT / D.death.seconds)
      drawSprite(ctx, 'z6_sovereign', this.pos.x, this.pos.y + (1 - k) * 12, {
        scale: 2.2 * (0.6 + 0.4 * k),
        alpha: Math.max(0.1, k),
        white: 0.5 * (1 - k),
      })
      return
    }

    const beat = this.combo === 'wind' || this.chargeWind > 0 ? 1 + 0.07 * Math.sin(t * 24) : 1 + 0.02 * Math.sin(t * 2.4)
    drawSprite(ctx, 'z6_sovereign', this.pos.x, this.pos.y + Math.sin(t * 1.2) * 2, {
      flip: this.lx < 0,
      scale: 2.2 * beat,
      white: this.hurtT > 0 ? 0.85 : this.combo === 'wind' ? 0.22 : 0,
    })

    if (this.glintT > 0) this.renderGlint(ctx)
    this.renderTelegraphs(d)
  }

  private renderGlint(ctx: CanvasRenderingContext2D): void {
    const k = 0.45 + 0.55 * Math.abs(Math.sin(this.anim * 28))
    const gx = this.pos.x + this.lx * 26
    const gy = this.pos.y + this.ly * 26 - 6
    ctx.save()
    ctx.globalAlpha = k
    ctx.fillStyle = '#ff4a4a'
    ctx.beginPath()
    ctx.moveTo(gx, gy - 8)
    ctx.lineTo(gx + 6, gy)
    ctx.lineTo(gx, gy + 8)
    ctx.lineTo(gx - 6, gy)
    ctx.closePath()
    ctx.fill()
    ctx.globalAlpha = 0.35 * k
    ctx.beginPath()
    ctx.arc(gx, gy, 15, 0, TAU)
    ctx.fill()
    ctx.restore()
  }

  /** The husk-heart: a hot ring, a clock, and a bar of its own. */
  private renderHeart(ctx: CanvasRenderingContext2D): void {
    const h = this.heart
    if (!h || !h.alive) return
    const beat = 0.5 + 0.5 * Math.sin(this.anim * 11)
    ctx.save()
    ctx.globalAlpha = 0.3 + 0.35 * beat
    ctx.fillStyle = C.heart
    ctx.beginPath()
    ctx.ellipse(h.pos.x, h.pos.y + 6, 30 + beat * 8, 15 + beat * 4, 0, 0, TAU)
    ctx.fill()
    ctx.globalAlpha = 0.9
    ctx.strokeStyle = '#ffd0e0'
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.arc(h.pos.x, h.pos.y, 22 + beat * 3, 0, TAU)
    ctx.stroke()

    // the five seconds, as a closing ring — the clearest clock in the game
    const k = Math.max(0, Math.min(1, this.heartT / D.heart.windowSeconds))
    ctx.globalAlpha = 1
    ctx.strokeStyle = k < 0.35 ? '#ff5a3c' : C.heart
    ctx.lineWidth = 4
    ctx.beginPath()
    ctx.arc(h.pos.x, h.pos.y, 34, -Math.PI / 2, -Math.PI / 2 + TAU * k)
    ctx.stroke()

    const w = 74
    const x = h.pos.x - w / 2
    const y = h.pos.y - 46
    ctx.fillStyle = '#00000099'
    ctx.fillRect(x - 1, y - 1, w + 2, 7)
    ctx.fillStyle = C.heart
    ctx.fillRect(x, y, w * Math.max(0, h.hp / Math.max(1, h.maxHp)), 5)
    ctx.font = '700 10px ui-monospace, "SF Mono", Menlo, monospace'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = '#000000'
    ctx.fillText(`SEAL IT — ${this.heartT.toFixed(1)}`, h.pos.x + 1, y - 8)
    ctx.fillStyle = '#ffd0e0'
    ctx.fillText(`SEAL IT — ${this.heartT.toFixed(1)}`, h.pos.x, y - 9)
    ctx.restore()
  }

  private renderGlobs(ctx: CanvasRenderingContext2D): void {
    ctx.save()
    for (const g of this.globs) {
      if (!g.on) continue
      const k = Math.min(1, g.t / g.dur)
      const x = g.x0 + (g.tx - g.x0) * k
      const y = g.y0 + (g.ty - g.y0) * k
      const lift = Math.sin(k * Math.PI) * D.glob.arcHeight

      // the landing marker IS the telegraph, and it is on the floor where the
      // damage will be
      ctx.globalAlpha = 0.22 + 0.4 * k
      ctx.strokeStyle = D.glob.color
      ctx.lineWidth = 2 + k * 2
      ctx.beginPath()
      ctx.arc(g.tx, g.ty, D.glob.splash, 0, TAU)
      ctx.stroke()
      ctx.globalAlpha = 0.12 + 0.2 * k
      ctx.fillStyle = D.glob.color
      ctx.beginPath()
      ctx.arc(g.tx, g.ty, D.glob.splash * k, 0, TAU)
      ctx.fill()

      ctx.globalAlpha = 0.3
      ctx.fillStyle = '#000000'
      ctx.beginPath()
      ctx.ellipse(x, y, 9, 4, 0, 0, TAU)
      ctx.fill()
      ctx.globalAlpha = 1
      ctx.fillStyle = D.glob.color
      ctx.beginPath()
      ctx.arc(x, y - lift, 8, 0, TAU)
      ctx.fill()
      ctx.fillStyle = '#e8ffd6'
      ctx.beginPath()
      ctx.arc(x - 2, y - lift - 2, 3, 0, TAU)
      ctx.fill()
    }
    ctx.restore()
  }

  // --------------------------------------------------------------- test seam

  debugState(): {
    phase: number
    memory: number
    hp: number
    maxHp: number
    escort: EscortPlan
    flood: { phase: FloodPhase; t: number; active: number[] }
    heart: { alive: boolean; t: number; resolved: boolean; hp: number }
    dark: boolean
    dying: boolean
    paid: boolean
    tookDamage: boolean
  } {
    return {
      phase: this.phase,
      memory: this.memory,
      hp: this.hp,
      maxHp: this.maxHp,
      escort: this.escort,
      flood: { phase: this.floodPhase, t: this.floodT, active: this.active.slice() },
      heart: {
        alive: this.heart != null && this.heart.alive,
        t: this.heartT,
        resolved: this.heartResolved,
        hp: this.heart ? this.heart.hp : 0,
      },
      dark: zone6Arena.dark,
      dying: this.dying,
      paid: this.paid,
      tookDamage: this.tookDamage,
    }
  }

  /** Force the next edge-flood to land now (test only). */
  debugForceFlood(): void {
    if (this.floodPhase === 'gap') this.floodT = 0
  }
}

// ===========================================================================
// sprites
// A towering echo of the Warden: the same hood, the same sash, the same held
// weapon — all of it wrong. Twice the height, a crown of black spikes it grew
// rather than earned, and a body that trails off into gloom instead of legs.
// ===========================================================================

const RD = Math.round

registerPainter('z6_sovereign', (a: PixelArt, _p, _pal) => {
  const s = a.size
  const cx = s / 2
  const cloak = '#2b1f47'
  const cloakLo = shade(cloak, -0.5)
  const cloakHi = shade(cloak, 0.2)
  const vein = C.vein
  const crown = '#120c1e'
  const eye = '#e6c9ff'

  // 1. THE TRAIL. It does not stand on the floor — the hem comes apart into the
  //    Gloom, so the silhouette has no feet and no bottom edge.
  for (let y = 0; y < RD(s * 0.2); y++) {
    const t = y / (s * 0.2)
    const w = RD(s * (0.2 - 0.17 * t))
    const wob = RD(Math.sin(y * 0.9) * s * 0.03)
    if (y % 3 === 2 && t > 0.4) continue // torn: gaps, not a taper
    a.hline(RD(cx - w + wob), RD(s * 0.78 + y), w * 2, t > 0.55 ? cloakLo : cloak)
  }
  for (let i = -2; i <= 2; i++) {
    if (i === 0) continue
    a.set(RD(cx + i * s * 0.09), RD(s * 0.96 + (i % 2) * 0.02 * s), cloakLo)
  }

  // 2. THE BODY. Tall and narrow — the Warden's proportions stretched, not a
  //    bell: a straight column from the mantle to the hem, veined down its front.
  for (let y = 0; y < RD(s * 0.42); y++) {
    const t = y / (s * 0.42)
    const w = RD(s * (0.15 + 0.09 * t))
    a.hline(RD(cx - w), RD(s * 0.4 + y), w * 2, y % 8 === 7 ? cloakLo : cloak)
  }
  a.vline(RD(cx - s * 0.06), RD(s * 0.46), RD(s * 0.3), cloakLo)
  a.vline(RD(cx + s * 0.05), RD(s * 0.46), RD(s * 0.3), cloakLo)

  // 3. THE MANTLE. Wide, angular, and set on the shoulders rather than bulging
  //    off them — the one place the shape is broader than a Warden's.
  for (let y = 0; y < RD(s * 0.1); y++) {
    const w = RD(s * (0.3 - 0.14 * (y / (s * 0.1))))
    a.hline(RD(cx - w), RD(s * 0.36 + y), w * 2, y === 0 ? cloakHi : cloak)
  }
  a.line(RD(cx - s * 0.3), RD(s * 0.37), RD(cx - s * 0.16), RD(s * 0.47), cloakLo)
  a.line(RD(cx + s * 0.29), RD(s * 0.37), RD(cx + s * 0.15), RD(s * 0.47), cloakLo)

  // 4. THE SASH the Warden wears in ember — here it is a vein, and it is lit.
  a.hline(RD(cx - s * 0.19), RD(s * 0.55), RD(s * 0.38), vein)
  a.hline(RD(cx - s * 0.16), RD(s * 0.56), RD(s * 0.32), shade(vein, -0.5))
  for (let i = -1; i <= 1; i++) {
    const x = RD(cx + i * s * 0.11)
    a.line(x, RD(s * 0.57), RD(x + i * 3), RD(s * 0.76), shade(vein, -0.55))
    a.set(RD(x + i * 3), RD(s * 0.77), vein)
  }

  // 5. THE HOOD. Pointed, deep, and empty except for the eyes — the Warden's
  //    read, elongated by half again.
  for (let y = 0; y < RD(s * 0.24); y++) {
    const t = y / (s * 0.24)
    const w = RD(s * (0.04 + 0.16 * Math.sqrt(t)))
    a.hline(RD(cx - w), RD(s * 0.14 + y), w * 2, cloak)
  }
  a.ellipse(cx, s * 0.31, s * 0.19, s * 0.1, cloak)
  a.ellipse(cx, s * 0.3, s * 0.13, s * 0.09, '#07040e')
  a.ellipse(cx - s * 0.13, s * 0.22, s * 0.03, s * 0.06, cloakHi)
  // two eyes, violet where the Warden's are ember
  a.rect(RD(cx - s * 0.09), RD(s * 0.29), 3, 3, eye)
  a.rect(RD(cx + s * 0.04), RD(s * 0.29), 3, 3, eye)
  a.set(RD(cx - s * 0.08), RD(s * 0.29), '#ffffff')
  a.set(RD(cx + s * 0.05), RD(s * 0.29), '#ffffff')

  // 6. THE CROWN. Five uneven points growing straight out of the hood, tipped in
  //    vein-light: the one thing on it that was never yours.
  const cy = RD(s * 0.16)
  a.rect(RD(cx - s * 0.17), cy, RD(s * 0.34), 3, crown)
  const spikes: [number, number][] = [
    [-0.17, 7],
    [-0.09, 12],
    [0, 17],
    [0.08, 12],
    [0.16, 7],
  ]
  for (const [ox, h] of spikes) {
    const x = RD(cx + ox * s)
    for (let k = 0; k < h; k++) {
      const w = k < h - 3 ? 2 : 1
      a.hline(x, cy - k, w, crown)
    }
    a.set(x, cy - h, vein)
    a.set(x, cy - h + 1, shade(vein, -0.4))
  }

  // 7. THE BLADE, held out the way you hold yours — the same silhouette, the
  //    same reach, and nothing warm anywhere on it.
  const wx = RD(cx + s * 0.3)
  a.vline(wx, RD(s * 0.14), RD(s * 0.5), '#3a2a52')
  a.vline(wx + 1, RD(s * 0.17), RD(s * 0.45), vein)
  a.vline(wx + 2, RD(s * 0.2), RD(s * 0.38), '#3a2a52')
  a.set(wx + 1, RD(s * 0.12), '#f0e0ff')
  a.hline(wx - 3, RD(s * 0.64), 8, crown)
  a.vline(wx, RD(s * 0.64), 5, crown)
})

registerRecipe('z6_sovereign', { key: 'z6_sovereign', size: 64 })

/** The husk-heart: a torn-out organ of flesh-stone, still beating. */
registerPainter('z6_huskheart', (a: PixelArt, p, _pal) => {
  const s = a.size
  const cx = s / 2
  const flesh = typeof p.flesh === 'string' ? p.flesh : '#5a2340'
  const vein = typeof p.vein === 'string' ? p.vein : C.vein
  const lo = shade(flesh, -0.45)
  const hi = shade(flesh, 0.3)

  a.ellipse(cx, s * 0.88, s * 0.34, s * 0.07, '#0a0614')
  // the two lobes and the point below them — a heart read from a silhouette
  a.ellipse(cx - s * 0.17, s * 0.36, s * 0.2, s * 0.2, lo)
  a.ellipse(cx + s * 0.17, s * 0.36, s * 0.2, s * 0.2, lo)
  a.ellipse(cx - s * 0.16, s * 0.34, s * 0.17, s * 0.17, flesh)
  a.ellipse(cx + s * 0.16, s * 0.34, s * 0.17, s * 0.17, flesh)
  for (let y = 0; y < RD(s * 0.42); y++) {
    const t = y / (s * 0.42)
    const w = RD(s * (0.33 - 0.31 * t))
    a.hline(RD(cx - w), RD(s * 0.42 + y), w * 2, y % 5 === 4 ? lo : flesh)
  }
  a.ellipse(cx - s * 0.1, s * 0.3, s * 0.07, s * 0.06, hi)
  // the veins, and the torn stubs of the vessels it was ripped from
  for (let i = -2; i <= 2; i++) {
    const x = RD(cx + i * s * 0.13)
    a.line(x, RD(s * 0.22), RD(x + i * 3), RD(s * 0.06), shade(vein, -0.3))
    a.set(RD(x + i * 3), RD(s * 0.05), vein)
    a.line(RD(cx), RD(s * 0.5), x, RD(s * 0.66), shade(vein, -0.55))
  }
  a.disc(cx, s * 0.46, s * 0.07, vein)
  a.set(RD(cx), RD(s * 0.46), '#ffe0f0')
})

registerRecipe('z6_huskheart', { key: 'z6_huskheart', size: 36 })

// ---------------------------------------------------------------- registry

let live: GloomSovereign | null = null

registerZoneContent({ enemies: ZONE6_BOSS_ENEMIES })
registerBossArena(D.id, ZONE6_BOSS_ARENA)
registerBoss(D.id, () => {
  live = new GloomSovereign()
  return live
})

/** TEST SEAM (test/zone6.mjs). Inert unless something calls it. */
export const boss6Debug = {
  state: (): ReturnType<GloomSovereign['debugState']> | null => (live ? live.debugState() : null),
  forceFlood: (): void => live?.debugForceFlood(),
  huskSeconds: ZONE6_PALE.huskSeconds,
}
